import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  es: {
    // Navbar
    nav: {
      home: 'Inicio',
      about: 'Acerca de',
      services: 'Servicios',
      contact: 'Contacto',
      billingAvailable: 'Facturación Disponible',
      bookNow: 'Reservar Ahora',
    },
    // Hero
    hero: {
      badge: 'Facturación en Todos los Servicios',
      title: 'Servicio Ejecutivo de',
      titleHighlight: 'Traslados',
      subtitle: 'Experimenta el máximo confort y puntualidad en cada viaje. Conductores profesionales, vehículos premium y atención personalizada.',
      features: {
        drivers: 'Conductores Verificados',
        punctuality: 'Puntualidad Garantizada',
        vehicles: 'Vehículos Premium',
        billing: 'Facturación Disponible',
      },
      cta: 'Reservar Ahora',
      ctaServices: 'Ver Servicios',
    },
    // Stats
    stats: {
      experience: 'Años de Experiencia',
      trips: 'Viajes Realizados',
      satisfaction: 'Clientes Satisfechos',
      availability: 'Disponibilidad',
    },
    // Services
    services: {
      sectionLabel: 'Nuestros Servicios',
      title: 'Soluciones de Transporte a tu Medida',
      subtitle: 'Ofrecemos servicios especializados para cada necesidad, siempre con la más alta calidad y profesionalismo.',
      learnMore: 'Conocer más',
      airport: {
        title: 'Traslados al Aeropuerto',
        subtitle: 'Llegadas y salidas sin estrés',
        description: 'Servicio de transporte ejecutivo desde y hacia el aeropuerto con monitoreo de vuelos en tiempo real. Nos adaptamos a cambios de horario y te esperamos con señalización personalizada.',
        features: [
          'Monitoreo de vuelos en tiempo real',
          'Espera sin costo adicional por retrasos',
          'Señalización personalizada',
          'Asistencia con equipaje',
          'Rutas optimizadas',
          'Vehículos con amplio espacio',
        ],
      },
      corporate: {
        title: 'Servicio Corporativo',
        subtitle: 'Transporte ejecutivo para empresas',
        description: 'Soluciones de transporte para empresas que demandan profesionalismo y eficiencia. Ofrecemos planes mensuales, facturación centralizada y atención personalizada para equipos de trabajo.',
        features: [
          'Planes empresariales a medida',
          'Facturación mensual centralizada',
          'Conductores asignados',
          'Reservaciones múltiples',
          'Informes de viaje detallados',
          'Atención 24/7 dedicada',
        ],
      },
      events: {
        title: 'Eventos Especiales',
        subtitle: 'Haz memorable cada ocasión',
        description: 'Transporte de lujo para bodas, graduaciones, conciertos, galas y cualquier evento especial. Vehículos decorados según la ocasión y servicio impecable para que tu evento sea inolvidable.',
        features: [
          'Decoración personalizada',
          'Coordinación con organizadores',
          'Múltiples vehículos disponibles',
          'Servicio de chofer elegante',
          'Horarios flexibles',
          'Bebidas de cortesía',
        ],
      },
      bookThis: 'Reservar Este Servicio',
      withInvoice: 'Con Factura',
      quoteTitle: '¿Necesitas una Cotización Personalizada?',
      quoteSubtitle: 'Contáctanos para obtener un presupuesto a la medida de tus necesidades.',
      requestQuote: 'Solicitar Cotización',
      bookToday: 'Reserva tu Servicio Hoy',
      bookTodaySubtitle: 'No importa cuál sea tu necesidad de transporte, tenemos la solución perfecta para ti. Todos nuestros servicios incluyen facturación fiscal.',
      makeReservation: 'Hacer una Reservación',
      contactUs: 'Contactar',
    },
    // Vehicle Features
    vehicle: {
      sectionLabel: 'Nuestra Flota',
      title: 'Vehículos de Primera Clase',
      subtitle: 'Todos nuestros vehículos están equipados con las mejores comodidades para tu confort.',
      wifi: 'WiFi de Alta Velocidad',
      wifiDesc: 'Conexión estable para trabajar en ruta',
      chargers: 'Cargadores USB',
      chargersDesc: 'Mantén tus dispositivos con carga',
      climate: 'Clima Controlado',
      climateDesc: 'Temperatura perfecta siempre',
      insurance: 'Seguro Premium',
      insuranceDesc: 'Cobertura completa en cada viaje',
    },
    // Why Us
    whyUs: {
      sectionLabel: '¿Por qué elegirnos?',
      title: 'Compromiso con la Excelencia',
      subtitle: 'En FastCar nos dedicamos a ofrecer un servicio de transporte ejecutivo que supera las expectativas. Cada detalle está pensado para garantizar tu comodidad y seguridad.',
      features: [
        'Vehículos de última generación',
        'Conductores profesionales certificados',
        'Seguimiento en tiempo real',
        'Facturación fiscal en todos los servicios',
        'Atención personalizada 24/7',
      ],
      cta: 'Conoce Más Sobre Nosotros',
      billingTitle: 'Facturación',
      billingSubtitle: '100% de servicios',
    },
    // CTA Section
    cta: {
      title: '¿Listo para Viajar con Estilo?',
      subtitle: 'Reserva tu próximo traslado y experimenta el servicio ejecutivo que mereces. Facturamos todos nuestros servicios.',
    },
    // About Page
    about: {
      sectionLabel: 'Acerca de Nosotros',
      title: 'Más de una Década de',
      titleHighlight: 'Excelencia',
      subtitle: 'En FastCar hemos construido nuestra reputación sobre la base de la confianza, la puntualidad y el servicio excepcional. Conoce nuestra historia.',
      mission: {
        title: 'Nuestra Misión',
        description: 'Proporcionar servicios de transporte ejecutivo de la más alta calidad, garantizando seguridad, puntualidad y comodidad en cada viaje. Nos comprometemos a superar las expectativas de nuestros clientes con un servicio personalizado y profesional.',
      },
      vision: {
        title: 'Nuestra Visión',
        description: 'Ser reconocidos como el servicio de transporte ejecutivo líder en la región, destacando por nuestra innovación, calidad de servicio y compromiso con la satisfacción del cliente. Aspiramos a establecer nuevos estándares en la industria del transporte premium.',
      },
      values: {
        sectionLabel: 'Nuestros Valores',
        title: 'Los Principios que nos Guían',
        subtitle: 'Estos valores fundamentales definen quiénes somos y cómo trabajamos cada día para ofrecerte el mejor servicio.',
        security: { title: 'Seguridad', description: 'Tu seguridad es nuestra prioridad número uno. Todos nuestros conductores están verificados y capacitados.' },
        punctuality: { title: 'Puntualidad', description: 'Valoramos tu tiempo. Garantizamos llegadas y salidas puntuales en cada servicio.' },
        excellence: { title: 'Excelencia', description: 'Nos esforzamos por superar tus expectativas en cada viaje con atención al detalle.' },
        commitment: { title: 'Compromiso', description: 'Nos comprometemos con la satisfacción total de nuestros clientes en cada servicio.' },
      },
      history: {
        sectionLabel: 'Nuestra Historia',
        title: 'Un Camino de Crecimiento',
        description: 'FastCar nació en 2014 con una visión clara: transformar el transporte ejecutivo en México. Desde entonces, hemos crecido constantemente, siempre manteniendo nuestro compromiso con la excelencia y la satisfacción del cliente.',
        milestones: [
          { year: '2014', event: 'Fundación de FastCar' },
          { year: '2016', event: 'Expansión a servicio corporativo' },
          { year: '2018', event: 'Certificación de calidad ISO 9001' },
          { year: '2020', event: 'Digitalización completa de servicios' },
          { year: '2023', event: '+50,000 viajes realizados' },
          { year: '2024', event: 'Flota 100% renovada' },
        ],
      },
      team: {
        sectionLabel: 'Nuestro Equipo',
        title: 'Profesionales Comprometidos',
        subtitle: 'Un equipo de expertos dedicados a brindarte la mejor experiencia de transporte ejecutivo.',
      },
      billing: {
        title: 'Facturación Fiscal Garantizada',
        description: 'Emitimos factura por el 100% de nuestros servicios para tu tranquilidad fiscal.',
        cta: 'Reservar con Factura',
      },
      stats: {
        clients: 'Clientes Corporativos',
        experience: 'Años de Experiencia',
        rating: 'Calificación Promedio',
        invoiced: 'Servicios Facturados',
      },
      ctaTitle: '¿Listo para Conocernos en Persona?',
      ctaSubtitle: 'Reserva tu próximo viaje y experimenta de primera mano nuestro compromiso con la excelencia.',
      ctaButton: 'Hacer una Reservación',
    },
    // Contact Page
    contact: {
      sectionLabel: 'Contacto',
      title: 'Estamos para',
      titleHighlight: 'Servirte',
      subtitle: '¿Tienes preguntas o quieres hacer una reservación? Contáctanos por el medio que prefieras. Estamos disponibles 24/7.',
      badge: 'Emitimos Factura Fiscal por Todos los Servicios',
      info: {
        phone: { title: 'Teléfono', description: 'Llámanos las 24 horas' },
        whatsapp: { title: 'WhatsApp', description: 'Escríbenos por WhatsApp' },
        email: { title: 'Email', description: 'Respuesta en 24 horas' },
        location: { title: 'Ubicación', description: 'Servicio en toda el área metropolitana' },
      },
      hours: {
        title: 'Horarios de Atención',
        weekdays: 'Lunes - Viernes',
        saturday: 'Sábado',
        sunday: 'Domingo',
        holidays: 'Días Festivos',
        allDay: '24 horas',
      },
      billing: {
        title: 'Facturación Fiscal',
        description: 'Emitimos factura por el 100% de nuestros servicios. Solo necesitas indicar en tu reservación que requieres factura y proporcionarnos tus datos fiscales.',
        features: ['Facturación inmediata', 'CFDI 4.0 compatible', 'Envío por correo electrónico'],
      },
      social: 'Síguenos',
      faq: {
        sectionLabel: 'Preguntas Frecuentes',
        title: '¿Tienes Dudas?',
        questions: [
          { q: '¿Cómo puedo hacer una reservación?', a: 'Puedes reservar a través de nuestro formulario en línea, por teléfono o WhatsApp. Te confirmaremos en menos de 30 minutos.' },
          { q: '¿Emiten factura fiscal?', a: 'Sí, emitimos factura por el 100% de nuestros servicios. Solo indícalo al hacer tu reservación.' },
          { q: '¿Cuál es la política de cancelación?', a: 'Puedes cancelar sin cargo hasta 4 horas antes del servicio programado.' },
          { q: '¿Qué formas de pago aceptan?', a: 'Aceptamos efectivo, tarjetas de crédito/débito y transferencias bancarias.' },
        ],
      },
    },
    // Reservation Form
    form: {
      title: 'Solicita tu Reservación',
      subtitle: 'Completa el formulario y te contactaremos',
      name: 'Nombre Completo',
      namePlaceholder: 'Juan Pérez',
      email: 'Correo Electrónico',
      emailPlaceholder: 'juan@empresa.com',
      phone: 'Teléfono',
      phonePlaceholder: '+52 55 1234 5678',
      serviceType: 'Tipo de Servicio',
      servicePlaceholder: 'Selecciona un servicio',
      serviceOptions: {
        airport: 'Traslado al Aeropuerto',
        corporate: 'Servicio Corporativo',
        events: 'Eventos Especiales',
      },
      pickup: 'Lugar de Recogida',
      pickupPlaceholder: 'Dirección o punto de referencia',
      dropoff: 'Lugar de Destino',
      dropoffPlaceholder: 'Dirección o punto de referencia',
      date: 'Fecha',
      time: 'Hora',
      passengers: 'Pasajeros',
      passengerSingular: 'pasajero',
      passengerPlural: 'pasajeros',
      notes: 'Notas Adicionales (Opcional)',
      notesPlaceholder: 'Información adicional, requerimientos especiales...',
      invoice: 'Requiero factura fiscal',
      submit: 'Solicitar Reservación',
      submitting: 'Enviando...',
      success: {
        title: '¡Reservación Enviada!',
        message: 'Nos pondremos en contacto contigo para confirmar los detalles.',
      },
      toast: {
        success: '¡Reservación enviada exitosamente!',
        successDesc: 'Nos pondremos en contacto contigo pronto.',
        error: 'Error al enviar la reservación',
        errorDesc: 'Por favor intenta de nuevo.',
      },
    },
    // Footer
    footer: {
      description: 'Servicio ejecutivo de traslados con la más alta calidad y puntualidad. Tu comodidad es nuestra prioridad.',
      quickLinks: 'Enlaces Rápidos',
      servicesTitle: 'Servicios',
      contactTitle: 'Contacto',
      rights: 'Todos los derechos reservados.',
      invoiceNote: 'Emitimos factura fiscal por todos nuestros servicios',
    },
  },
  en: {
    // Navbar
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      contact: 'Contact',
      billingAvailable: 'Invoice Available',
      bookNow: 'Book Now',
    },
    // Hero
    hero: {
      badge: 'Invoice Available for All Services',
      title: 'Executive',
      titleHighlight: 'Transportation',
      subtitle: 'Experience maximum comfort and punctuality on every trip. Professional drivers, premium vehicles, and personalized attention.',
      features: {
        drivers: 'Verified Drivers',
        punctuality: 'Guaranteed Punctuality',
        vehicles: 'Premium Vehicles',
        billing: 'Invoice Available',
      },
      cta: 'Book Now',
      ctaServices: 'View Services',
    },
    // Stats
    stats: {
      experience: 'Years of Experience',
      trips: 'Trips Completed',
      satisfaction: 'Satisfied Clients',
      availability: 'Availability',
    },
    // Services
    services: {
      sectionLabel: 'Our Services',
      title: 'Transportation Solutions Tailored to You',
      subtitle: 'We offer specialized services for every need, always with the highest quality and professionalism.',
      learnMore: 'Learn more',
      airport: {
        title: 'Airport Transfers',
        subtitle: 'Stress-free arrivals and departures',
        description: 'Executive transportation service to and from the airport with real-time flight monitoring. We adapt to schedule changes and wait for you with personalized signage.',
        features: [
          'Real-time flight monitoring',
          'Free waiting for delays',
          'Personalized signage',
          'Luggage assistance',
          'Optimized routes',
          'Spacious vehicles',
        ],
      },
      corporate: {
        title: 'Corporate Service',
        subtitle: 'Executive transportation for businesses',
        description: 'Transportation solutions for companies that demand professionalism and efficiency. We offer monthly plans, centralized billing, and personalized attention for work teams.',
        features: [
          'Custom business plans',
          'Centralized monthly billing',
          'Assigned drivers',
          'Multiple reservations',
          'Detailed trip reports',
          'Dedicated 24/7 support',
        ],
      },
      events: {
        title: 'Special Events',
        subtitle: 'Make every occasion memorable',
        description: 'Luxury transportation for weddings, graduations, concerts, galas, and any special event. Vehicles decorated according to the occasion and impeccable service to make your event unforgettable.',
        features: [
          'Personalized decoration',
          'Coordination with organizers',
          'Multiple vehicles available',
          'Elegant chauffeur service',
          'Flexible schedules',
          'Complimentary beverages',
        ],
      },
      bookThis: 'Book This Service',
      withInvoice: 'With Invoice',
      quoteTitle: 'Need a Custom Quote?',
      quoteSubtitle: 'Contact us to get a quote tailored to your needs.',
      requestQuote: 'Request Quote',
      bookToday: 'Book Your Service Today',
      bookTodaySubtitle: 'No matter what your transportation needs are, we have the perfect solution for you. All our services include tax invoicing.',
      makeReservation: 'Make a Reservation',
      contactUs: 'Contact',
    },
    // Vehicle Features
    vehicle: {
      sectionLabel: 'Our Fleet',
      title: 'First-Class Vehicles',
      subtitle: 'All our vehicles are equipped with the best amenities for your comfort.',
      wifi: 'High-Speed WiFi',
      wifiDesc: 'Stable connection to work on the road',
      chargers: 'USB Chargers',
      chargersDesc: 'Keep your devices charged',
      climate: 'Climate Control',
      climateDesc: 'Perfect temperature always',
      insurance: 'Premium Insurance',
      insuranceDesc: 'Full coverage on every trip',
    },
    // Why Us
    whyUs: {
      sectionLabel: 'Why Choose Us?',
      title: 'Commitment to Excellence',
      subtitle: 'At FastCar, we are dedicated to offering executive transportation service that exceeds expectations. Every detail is designed to ensure your comfort and safety.',
      features: [
        'Latest generation vehicles',
        'Certified professional drivers',
        'Real-time tracking',
        'Tax invoicing on all services',
        'Personalized 24/7 attention',
      ],
      cta: 'Learn More About Us',
      billingTitle: 'Invoicing',
      billingSubtitle: '100% of services',
    },
    // CTA Section
    cta: {
      title: 'Ready to Travel in Style?',
      subtitle: 'Book your next transfer and experience the executive service you deserve. We invoice all our services.',
    },
    // About Page
    about: {
      sectionLabel: 'About Us',
      title: 'More Than a Decade of',
      titleHighlight: 'Excellence',
      subtitle: 'At FastCar, we have built our reputation on the foundation of trust, punctuality, and exceptional service. Learn our story.',
      mission: {
        title: 'Our Mission',
        description: 'To provide the highest quality executive transportation services, ensuring safety, punctuality, and comfort on every trip. We are committed to exceeding our clients\' expectations with personalized and professional service.',
      },
      vision: {
        title: 'Our Vision',
        description: 'To be recognized as the leading executive transportation service in the region, standing out for our innovation, service quality, and commitment to customer satisfaction. We aspire to set new standards in the premium transportation industry.',
      },
      values: {
        sectionLabel: 'Our Values',
        title: 'The Principles That Guide Us',
        subtitle: 'These fundamental values define who we are and how we work every day to offer you the best service.',
        security: { title: 'Safety', description: 'Your safety is our number one priority. All our drivers are verified and trained.' },
        punctuality: { title: 'Punctuality', description: 'We value your time. We guarantee punctual arrivals and departures on every service.' },
        excellence: { title: 'Excellence', description: 'We strive to exceed your expectations on every trip with attention to detail.' },
        commitment: { title: 'Commitment', description: 'We are committed to the total satisfaction of our clients on every service.' },
      },
      history: {
        sectionLabel: 'Our History',
        title: 'A Path of Growth',
        description: 'FastCar was born in 2014 with a clear vision: to transform executive transportation in Mexico. Since then, we have grown constantly, always maintaining our commitment to excellence and customer satisfaction.',
        milestones: [
          { year: '2014', event: 'FastCar Founded' },
          { year: '2016', event: 'Corporate service expansion' },
          { year: '2018', event: 'ISO 9001 quality certification' },
          { year: '2020', event: 'Complete service digitalization' },
          { year: '2023', event: '+50,000 trips completed' },
          { year: '2024', event: '100% fleet renewal' },
        ],
      },
      team: {
        sectionLabel: 'Our Team',
        title: 'Committed Professionals',
        subtitle: 'A team of experts dedicated to providing you with the best executive transportation experience.',
      },
      billing: {
        title: 'Guaranteed Tax Invoicing',
        description: 'We issue invoices for 100% of our services for your tax peace of mind.',
        cta: 'Book with Invoice',
      },
      stats: {
        clients: 'Corporate Clients',
        experience: 'Years of Experience',
        rating: 'Average Rating',
        invoiced: 'Invoiced Services',
      },
      ctaTitle: 'Ready to Meet Us in Person?',
      ctaSubtitle: 'Book your next trip and experience firsthand our commitment to excellence.',
      ctaButton: 'Make a Reservation',
    },
    // Contact Page
    contact: {
      sectionLabel: 'Contact',
      title: 'We\'re Here to',
      titleHighlight: 'Serve You',
      subtitle: 'Have questions or want to make a reservation? Contact us through your preferred method. We\'re available 24/7.',
      badge: 'We Issue Tax Invoices for All Services',
      info: {
        phone: { title: 'Phone', description: 'Call us 24 hours' },
        whatsapp: { title: 'WhatsApp', description: 'Message us on WhatsApp' },
        email: { title: 'Email', description: 'Response within 24 hours' },
        location: { title: 'Location', description: 'Service throughout the metropolitan area' },
      },
      hours: {
        title: 'Business Hours',
        weekdays: 'Monday - Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
        holidays: 'Holidays',
        allDay: '24 hours',
      },
      billing: {
        title: 'Tax Invoicing',
        description: 'We issue invoices for 100% of our services. You only need to indicate in your reservation that you require an invoice and provide us with your tax details.',
        features: ['Immediate invoicing', 'CFDI 4.0 compatible', 'Email delivery'],
      },
      social: 'Follow Us',
      faq: {
        sectionLabel: 'Frequently Asked Questions',
        title: 'Have Questions?',
        questions: [
          { q: 'How can I make a reservation?', a: 'You can book through our online form, by phone, or WhatsApp. We\'ll confirm within 30 minutes.' },
          { q: 'Do you issue tax invoices?', a: 'Yes, we issue invoices for 100% of our services. Just indicate it when making your reservation.' },
          { q: 'What is the cancellation policy?', a: 'You can cancel free of charge up to 4 hours before the scheduled service.' },
          { q: 'What payment methods do you accept?', a: 'We accept cash, credit/debit cards, and bank transfers.' },
        ],
      },
    },
    // Reservation Form
    form: {
      title: 'Request Your Reservation',
      subtitle: 'Complete the form and we\'ll contact you',
      name: 'Full Name',
      namePlaceholder: 'John Doe',
      email: 'Email',
      emailPlaceholder: 'john@company.com',
      phone: 'Phone',
      phonePlaceholder: '+1 555 123 4567',
      serviceType: 'Service Type',
      servicePlaceholder: 'Select a service',
      serviceOptions: {
        airport: 'Airport Transfer',
        corporate: 'Corporate Service',
        events: 'Special Events',
      },
      pickup: 'Pickup Location',
      pickupPlaceholder: 'Address or reference point',
      dropoff: 'Destination',
      dropoffPlaceholder: 'Address or reference point',
      date: 'Date',
      time: 'Time',
      passengers: 'Passengers',
      passengerSingular: 'passenger',
      passengerPlural: 'passengers',
      notes: 'Additional Notes (Optional)',
      notesPlaceholder: 'Additional information, special requirements...',
      invoice: 'I need a tax invoice',
      submit: 'Request Reservation',
      submitting: 'Sending...',
      success: {
        title: 'Reservation Sent!',
        message: 'We\'ll contact you to confirm the details.',
      },
      toast: {
        success: 'Reservation sent successfully!',
        successDesc: 'We\'ll contact you soon.',
        error: 'Error sending reservation',
        errorDesc: 'Please try again.',
      },
    },
    // Footer
    footer: {
      description: 'Executive transportation service with the highest quality and punctuality. Your comfort is our priority.',
      quickLinks: 'Quick Links',
      servicesTitle: 'Services',
      contactTitle: 'Contact',
      rights: 'All rights reserved.',
      invoiceNote: 'We issue tax invoices for all our services',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('fastcar-language');
    return saved || 'es';
  });

  useEffect(() => {
    localStorage.setItem('fastcar-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'es' ? 'en' : 'es'));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, translations: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
